import { apiClient } from './api';

export interface Campaign {
  id: string;
  name: string;
  type?: string;
  channel?: string;
  status?: string;
  targetAudience?: unknown;
  scheduledAt?: string;
  message?: unknown;
  metrics?: {
    sent?: number;
    delivered?: number;
    opened?: number;
    clicked?: number;
    converted?: number;
    failed?: number;
    skipped?: number;
  };
  createdAt: string;
}

export interface CustomerSegmentFilters {
  status?: string[];
  ageRange?: { min?: number; max?: number };
  visitCountRange?: { min?: number; max?: number };
  totalSpentRange?: { min?: number; max?: number };
  lastVisitDays?: number;
  loyaltyPointsRange?: { min?: number; max?: number };
  services?: string[];
  location?: string[];
  gender?: string;
  birthMonth?: number;
  anniversaryMonth?: number;
}

export interface CreateCampaignPayload {
  name: string;
  type: 'PROMOTIONAL' | 'REMINDER' | 'BIRTHDAY' | 'FEEDBACK';
  channel: 'SMS' | 'WHATSAPP' | 'EMAIL' | 'PUSH';
  targetAudience: {
    customerSegment: string;
    filters: CustomerSegmentFilters;
  };
  message: {
    subject?: string;
    content: string;
    mediaUrl?: string;
    // WhatsApp Business Cloud API template message (optional). When set,
    // backend sends `sendTemplateMessage` instead of freeform.
    templateName?: string;
    templateLanguage?: string;
    templateVariables?: string[];
  };
  scheduledAt: string;
}

class CampaignService {
  private baseUrl = '/campaigns';

  async getCampaigns(): Promise<Campaign[]> {
    const response = await apiClient.get<{ data?: Campaign[] }>(this.baseUrl);
    const list = response?.data;
    return Array.isArray(list) ? list : [];
  }

  async getCampaignById(id: string): Promise<Campaign | null> {
    const response = await apiClient.get<{ data?: Campaign }>(`${this.baseUrl}/${id}`);
    return response?.data ?? null;
  }

  async createCampaign(payload: CreateCampaignPayload): Promise<Campaign> {
    const response = await apiClient.post<{ data?: Campaign }>(this.baseUrl, payload);
    if (!response?.data) {
      throw new Error('Campaign create returned no data');
    }
    return response.data;
  }

  async updateCampaign(
    id: string,
    payload: Partial<CreateCampaignPayload> & { status?: string }
  ): Promise<Campaign> {
    const response = await apiClient.put<{ data?: Campaign }>(`${this.baseUrl}/${id}`, payload);
    if (!response?.data) {
      throw new Error('Campaign update returned no data');
    }
    return response.data;
  }

  async deleteCampaign(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async sendCampaign(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/send`);
  }

  async previewAudience(filters: CustomerSegmentFilters): Promise<{
    customers: unknown[];
    totalCount: number;
    previewCount: number;
  }> {
    const response = await apiClient.post<{
      data?: { customers: unknown[]; totalCount: number; previewCount: number };
    }>(`${this.baseUrl}/preview-audience`, { filters });
    const d = response?.data;
    return {
      customers: d?.customers ?? [],
      totalCount: d?.totalCount ?? 0,
      previewCount: d?.previewCount ?? 0
    };
  }
}

export const campaignService = new CampaignService();
